/**
 * The **one** age-fade schedule, shared by the object-track polylines and the
 * detection glyphs (REQ-UI-168; ADR-041 D-4 / D-7).
 *
 * # Why this module exists
 *
 * REQ-UI-167's Statement says the track line fades "with the head's age **on
 * the same schedule as detection glyphs**". Detection glyphs did not fade at
 * all — their ``icon-opacity`` was data-driven on ``anchored`` and nothing
 * else — so the clause presupposed a schedule that was never built, and the
 * line's three steps lived as a private table in the track layer. A track
 * that had gone quiet therefore showed a faint line ending in a
 * full-strength glyph (architect direction 2026-10-09: make the glyphs fade
 * too, for consistency).
 *
 * Holding the thresholds here makes "the same schedule" true **by
 * construction** rather than by two tables happening to agree
 * (CLAUDE.md §4.4). Each layer still owns its own *values* — the line's
 * opacities and the glyph's relative factors are different quantities — but
 * neither owns the fractions at which they change.
 *
 * # The schedule
 *
 * Age is expressed as a fraction of the deployment's live window
 * (``live.window_seconds`` on ``GET /api/config``; ADR-041 D-7), clamped to
 * ``[0, 1]``, and fades in three **steps** rather than a continuous ramp so
 * that "this one has gone quiet" reads at a glance (CLAUDE.md §8.1 operator
 * visibility) — and, as importantly, so the rendering has only three distinct
 * states per feature: a feature ageing *within* a step is not a visual change,
 * which is what lets the map sources diff instead of reposting (feature spec
 * ``ui-track-load-headroom`` §2.4).
 *
 * Cross-references:
 *   - REQ-UI-167 / -168 (the line's fade and the head-only glyph rule);
 *     REQ-UI-068 (the detection glyph).
 *   - map/object-track-layer-impl.ts (the line's opacities).
 *   - map/detection-layer-impl.ts (the glyph's relative factors).
 */
import type { ExpressionSpecification } from "maplibre-gl";

/**
 * The age fractions at which the fade steps. The first is always ``0`` (the
 * fresh band); each later entry is the lower bound of its band.
 */
export const AGE_FADE_STEP_FRACTIONS = [0, 0.5, 0.8] as const;

/**
 * The number of bands — the step index is in ``[0, AGE_FADE_BANDS - 1]``.
 */
export const AGE_FADE_BANDS = AGE_FADE_STEP_FRACTIONS.length;

/**
 * Opacity **relative to fresh** in each band, for a layer whose base opacity
 * carries another meaning it must keep (the detection glyph's located-vs-
 * anchored distinction — REQ-UI-071). A layer with no such base paints the
 * absolute values instead; see :data:`OBJECT_TRACK_AGE_OPACITIES`.
 */
export const AGE_FADE_FACTORS = [1, 0.7, 0.35] as const;

/**
 * The coarsest a fade re-evaluation may be, with no store change, so a feature
 * that has stopped reporting keeps fading. Coarse on purpose at the shipped
 * default window: the bands are 50 % and 80 % of 10 minutes, so a few seconds'
 * granularity is invisible.
 */
export const AGE_FADE_TICK_CEILING_MS = 15_000;

/** The finest — a tick below this is churn for no visible gain. */
export const AGE_FADE_TICK_FLOOR_MS = 1_000;

/**
 * How often to re-evaluate the fade for a given live window.
 *
 * A fixed 15 s was fine for the 10-minute default (narrowest band 120 s) and
 * wrong at the other end of the range: the BFF accepts a live window as short
 * as **60 s** (``MIN_LIVE_WINDOW_SECONDS``), whose narrowest band is 12 s, so
 * a quiet feature could sit a full quarter of the window in the wrong band —
 * and the fade exists precisely to say "this one has gone quiet" at a glance
 * (CLAUDE.md §8.1). The tick is therefore a third of the narrowest band,
 * clamped: at the default window that is 40 s → the unchanged 15 s ceiling, and
 * at the 60 s minimum it is 4 s.
 */
export function ageFadeTickMs(liveWindowMs: number): number {
  let narrowest = 1 - AGE_FADE_STEP_FRACTIONS[AGE_FADE_STEP_FRACTIONS.length - 1];
  for (let i = 1; i < AGE_FADE_STEP_FRACTIONS.length; i += 1) {
    narrowest = Math.min(narrowest, AGE_FADE_STEP_FRACTIONS[i] - AGE_FADE_STEP_FRACTIONS[i - 1]);
  }
  // Rounded: an interval is whole milliseconds, and the band fractions are
  // not exactly representable (0.8 − 0.5 is 0.30000000000000004), so an
  // unrounded third lands a hair under its intended value.
  const third = Math.round((Math.max(liveWindowMs, 0) * narrowest) / 3);
  return Math.min(AGE_FADE_TICK_CEILING_MS, Math.max(AGE_FADE_TICK_FLOOR_MS, third));
}

/** Age as a fraction of the live window, clamped to ``[0, 1]``. */
export function ageFraction(ageMs: number, liveWindowMs: number): number {
  const window = Math.max(liveWindowMs, 1);
  return Math.min(1, Math.max(0, ageMs / window));
}

/**
 * The band a fraction falls in — ``0`` fresh … ``AGE_FADE_BANDS - 1`` stale.
 *
 * This is what a feature carries (or what a source diff buckets on): two ages
 * in the same band are the same picture, so the renderer must not be told
 * about the difference.
 */
export function ageStepIndex(fraction: number): number {
  let index = 0;
  for (let i = 1; i < AGE_FADE_STEP_FRACTIONS.length; i += 1) {
    if (fraction >= AGE_FADE_STEP_FRACTIONS[i]) {
      index = i;
    }
  }
  return index;
}

/** The band a given age falls in, straight from milliseconds. */
export function ageStepFor(ageMs: number, liveWindowMs: number): number {
  return ageStepIndex(ageFraction(ageMs, liveWindowMs));
}

function assertBandValues(values: readonly number[]): void {
  if (values.length !== AGE_FADE_BANDS) {
    throw new Error(
      `age-fade expects ${String(AGE_FADE_BANDS)} values, one per band; got ${String(values.length)}`,
    );
  }
}

/**
 * A MapLibre ``step`` expression over a feature's **continuous**
 * ``age_fraction`` property, one value per band.
 *
 * Used by the track line, whose features carry the fraction itself.
 */
export function composeAgeFractionExpression(values: readonly number[]): ExpressionSpecification {
  assertBandValues(values);
  const expression: unknown[] = ["step", ["get", "age_fraction"], values[0]];
  for (let i = 1; i < AGE_FADE_STEP_FRACTIONS.length; i += 1) {
    expression.push(AGE_FADE_STEP_FRACTIONS[i], values[i]);
  }
  return expression as ExpressionSpecification;
}

/**
 * A MapLibre ``step`` expression over a feature's **banded** ``age_step``
 * property, one value per band.
 *
 * Used by the detection glyphs, whose features carry the band index rather
 * than the fraction — so the source's ordinary property signature already
 * changes only on a band crossing and needs no custom diff bucket. A feature
 * with no ``age_step`` (the fresh band, omitted to keep a located feature's
 * property shape unchanged) reads as band 0.
 */
export function composeAgeStepExpression(values: readonly number[]): ExpressionSpecification {
  assertBandValues(values);
  const expression: unknown[] = ["step", ["coalesce", ["get", "age_step"], 0], values[0]];
  for (let i = 1; i < AGE_FADE_STEP_FRACTIONS.length; i += 1) {
    expression.push(i, values[i]);
  }
  return expression as ExpressionSpecification;
}
